/**
 * services/AccountService.ts
 * Service for managing trading account entities with broker linkage
 *
 * Accounts represent individual trading accounts linked to brokers.
 * Extends BaseService to provide CRUD operations with status-based filtering
 * and broker-based queries. Includes atomic account creation with optional
 * starting balance via Dexie transaction nesting.
 */

import { BaseService } from './BaseService';
import type { Account } from '@/types/schema/Account';
import type { EntityStatus } from '@/types/shared/EntityStatus';
import { isEntityActive } from '@/types/shared/EntityStatus';
import { db } from '@/lib/db';
import { markAsInstrumentedWriteContext } from './sync/SyncImportContext';
import { balanceHistoryService } from './BalanceHistoryService';

/**
 * Service for managing trading account entities
 *
 * Provides CRUD operations plus broker-based filtering, status-based filtering,
 * and atomic account creation with starting balance.
 * Inherits getAll(), get(), update(), softDelete() from BaseService.
 */
export class AccountService extends BaseService<Account> {
  constructor() {
    super('accounts');
  }

  /**
   * Add a new account with default values
   *
   * Defaults to status: 'active' if not provided.
   *
   * @param data - Partial account data
   * @returns Promise resolving to the created account
   */
  override async add(data: Partial<Account>): Promise<Account> {
    return super.add({ status: 'active', ...data });
  }

  /**
   * Get accounts by broker ID
   *
   * Filters accounts linked to the specified broker.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @param brokerId - The broker ID to filter by
   * @returns Promise resolving to array of accounts linked to this broker
   */
  async getByBrokerId(brokerId: string): Promise<Account[]> {
    const allAccounts = await this.getAll();
    return allAccounts.filter(account => account.brokerId === brokerId);
  }

  /**
   * Get accounts by status
   *
   * Filters accounts by the specified EntityStatus.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @param status - The status to filter by ('active' | 'inactive' | 'archived')
   * @returns Promise resolving to array of accounts with matching status
   */
  async getByStatus(status: EntityStatus): Promise<Account[]> {
    const allAccounts = await this.getAll();
    return allAccounts.filter(account => account.status === status);
  }

  /**
   * Get all active accounts
   *
   * Convenience method for getting accounts where status is 'active'.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @returns Promise resolving to array of active accounts
   */
  async getActiveAccounts(): Promise<Account[]> {
    const allAccounts = await this.getAll();
    return allAccounts.filter(account => isEntityActive(account.status));
  }

  /**
   * Create an account with an optional starting balance
   *
   * Creates the account and optionally adds an initial BalanceHistory record
   * representing the starting balance. Both operations are performed atomically
   * within a single Dexie transaction - if either fails, both are rolled back.
   *
   * @param accountData - Partial account data (must include required fields: name, accountType, brokerId)
   * @param startingBalance - Optional starting balance as a pre-scaled integer.
   *   For example, $100.00 should be passed as 10000 (with default precision 2).
   *   If undefined, no BalanceHistory record is created.
   * @returns Promise resolving to the created account
   * @throws Error if required account fields are missing (validated at DB layer)
   */
  async createWithStartingBalance(
    accountData: Partial<Account>,
    startingBalance?: number
  ): Promise<Account> {
    let createdAccount: Account | undefined;

    await db.transaction(
      'rw',
      [db.accounts, db.balance_histories, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();

        // Create the account (nested transaction joins parent)
        createdAccount = await this.add(accountData);

        // If starting balance provided, create the initial balance history record
        if (startingBalance !== undefined) {
          await balanceHistoryService.addTransaction({
            accountId: createdAccount.id,
            timestamp: createdAccount.createdAt,
            transactionType: 'adjustment',
            amount: startingBalance,
            description: 'Starting balance',
          });
        }
      }
    );

    if (!createdAccount) {
      throw new Error('Failed to create account');
    }

    return createdAccount;
  }
}

// Export singleton instance
export const accountService = new AccountService();
